"use client";

import { ListingPreview } from "@/components/listings/listing-preview";
import {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import type { DetailedListingListItem } from "@/lib/types";
import { buildListingUrl } from "@/lib/url";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export type SimilarListingsSectionProps = {
  listings: DetailedListingListItem[];
  /** Overrides the default heading (e.g. to add the city for SEO). */
  title?: string;
  id?: string;
};

export function SimilarListingsSection({
  listings,
  title,
  id,
}: SimilarListingsSectionProps) {
  const t = useTranslations();
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const updateScrollState = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
    };

    updateScrollState();
    carouselApi.on("select", updateScrollState);
    carouselApi.on("reInit", updateScrollState);

    return () => {
      carouselApi.off("select", updateScrollState);
      carouselApi.off("reInit", updateScrollState);
    };
  }, [carouselApi]);

  if (listings.length === 0) {
    return null;
  }

  return (
    <section id={id} className="flex flex-col gap-5 scroll-mt-24">
      <h2 className="text-xl font-semibold tracking-tight text-foreground lg:text-[22px]">
        {title ?? t("saas.listing.similarListings.title")}
      </h2>

      <Carousel
        opts={{
          align: "start",
          dragFree: true,
        }}
        setApi={setCarouselApi}
        className="w-full"
      >
        <div
          className={cn(
            "pointer-events-none absolute inset-y-0 left-0 z-[5] w-6 bg-gradient-to-r from-white to-transparent transition-opacity md:w-8",
            canScrollPrev ? "opacity-100" : "opacity-0",
          )}
        />
        <div
          className={cn(
            "pointer-events-none absolute inset-y-0 right-0 z-[5] w-10 bg-gradient-to-l from-white via-white/90 to-transparent transition-opacity md:w-14",
            canScrollNext ? "opacity-100" : "opacity-0",
          )}
        />

        <CarouselContent className="-ml-3 md:-ml-4 items-stretch">
          {listings.map((listing) => (
            <CarouselItem
              key={listing.id}
              className="pl-3 md:pl-4 basis-[200px] sm:basis-[228px] md:basis-[300px] flex"
            >
              <Link href={buildListingUrl(listing)} className="block h-full w-full">
                <ListingPreview
                  listing={listing}
                  noSeenOrContactedBadge
                  className="min-h-[280px] sm:min-h-[308px] md:min-h-[320px]"
                />
              </Link>
            </CarouselItem>
          ))}
        </CarouselContent>

        {canScrollPrev && (
          <button
            type="button"
            aria-label={t("common.previous")}
            className="hidden md:inline-flex absolute left-2 top-1/2 -translate-y-1/2 z-20 h-9 w-9 items-center justify-center rounded-full border border-border bg-background/95 text-foreground shadow-sm transition-colors hover:bg-muted"
            onClick={() => carouselApi?.scrollPrev()}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}

        {canScrollNext && (
          <button
            type="button"
            aria-label={t("common.next")}
            className="hidden md:inline-flex absolute right-2 top-1/2 -translate-y-1/2 z-20 h-9 w-9 items-center justify-center rounded-full border border-border bg-background/95 text-foreground shadow-sm transition-colors hover:bg-muted"
            onClick={() => carouselApi?.scrollNext()}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </Carousel>
    </section>
  );
}
